import { H as HasElementTags } from './unhead.fg-0ge_u.mjs';
import { b as normalizeProps, d as dedupeKey, h as hashTag, r as resolveTags, i as isMetaArrayDupeKey } from './unhead.DiRbsb3I.mjs';
import { a as callHook } from './unhead.DvIxXxuO.mjs';

const WHITESPACE_RE = /\s+/;
// @__NO_SIDE_EFFECTS__
function createDomRenderer(options = {}) {
  return (head) => _renderDOMHead(head, options);
}
function renderDOMHead(head, options = {}) {
  return _renderDOMHead(head, options);
}
function _renderDOMHead(head, options = {}) {
  const dom = options.document || head.resolvedOptions.document;
  if (!dom || !head.dirty && ![...head.entries.values()].some((e) => e._pending !== void 0))
    return false;
  const beforeRenderCtx = { shouldRender: true, tags: [] };
  callHook(head, "dom:beforeRender", beforeRenderCtx);
  if (!beforeRenderCtx.shouldRender || head._du)
    return false;
  head._du = true;
  let state = head._dom;
  if (!state) {
    state = { _t: dom.title, _e: /* @__PURE__ */ new Map([["htmlAttrs", dom.documentElement], ["bodyAttrs", dom.body]]), _p: {}, _s: {} };
    for (const el of [...dom.body.children, ...dom.head.children]) {
      const tag = el.tagName.toLowerCase();
      if (!HasElementTags.has(tag))
        continue;
      const props = { innerHTML: el.innerHTML };
      for (const n of el.getAttributeNames())
        props[n] = el.getAttribute(n);
      const next = normalizeProps({ tag, props: {} }, props);
      next.key = el.getAttribute("data-hid") || void 0;
      let k = next._d = dedupeKey(next) || hashTag(next);
      let c = 1;
      while (state._e.has(k))
        k = `${next._d}:${c++}`;
      state._e.set(k, el);
    }
    for (const entry of head.entries.values()) {
      if (entry._o !== void 0) {
        const orig = entry._o;
        for (const t of ["bodyAttrs", "htmlAttrs"]) {
          const cls = orig[t]?.class;
          if (typeof cls === "string") {
            const $el = state._e.get(t);
            for (const c of cls.split(WHITESPACE_RE)) {
              if (c)
                state._p[`${t}:attr:class:${c}`] = () => $el.classList.remove(c);
            }
          }
        }
        delete entry._o;
      }
    }
  } else {
    state._p = { ...state._s };
  }
  state._s = {};
  function track(id, scope, fn) {
    const k = `${id}:${scope}`;
    state._s[k] = fn;
    delete state._p[k];
  }
  function trackCtx({ id, $el, tag }) {
    const isAttr = tag.tag.endsWith("Attrs");
    state._e.set(id, $el);
    if (!isAttr) {
      if (tag.textContent && tag.textContent !== $el.textContent)
        $el.textContent = tag.textContent;
      if (tag.innerHTML && tag.innerHTML !== $el.innerHTML)
        $el.innerHTML = tag.innerHTML;
      track(id, "el", () => {
        $el?.remove();
        state._e.delete(id);
      });
    }
    for (const k in tag.props) {
      const v = tag.props[k];
      if (k[0] === "o" && k[1] === "n" && typeof v === "function") {
        const ev = k.slice(2);
        if ($el?.dataset?.[`${k}fired`])
          v.call($el, new Event(ev));
        if ($el.getAttribute(`data-${k}`) !== "") {
          (tag.tag === "bodyAttrs" ? dom.defaultView : $el).addEventListener(ev, v.bind($el));
          $el.setAttribute(`data-${k}`, "");
        }
        continue;
      }
      const ck = `attr:${k}`;
      if (k === "class" && v) {
        for (const c of v) {
          if (isAttr)
            track(id, `${ck}:${c}`, () => $el.classList.remove(c));
          if (!$el.classList.contains(c))
            $el.classList.add(c);
        }
      } else if (k === "style" && v) {
        for (const [sk, sv] of v) {
          track(id, `${ck}:${sk}`, () => $el.style.removeProperty(sk));
          $el.style.setProperty(sk, sv);
        }
      } else if (v !== false && v !== null) {
        if ($el.getAttribute(k) !== v)
          $el.setAttribute(k, v === true ? "" : String(v));
        if (isAttr)
          track(id, ck, () => $el.removeAttribute(k));
      }
    }
  }
  const pending = [];
  const frag = {};
  const rawTags = resolveTags(head, options.tagWeight ? { tagWeight: options.tagWeight } : void 0);
  const tags = [];
  const dupeKeyCounter = /* @__PURE__ */ new Map();
  for (const tag of rawTags) {
    const count = dupeKeyCounter.get(tag._d) || 0;
    const id = (count ? `${tag._d}:${count}` : tag._d) || tag._h;
    const ctx = { tag, id, shouldRender: true };
    if (tag._d && isMetaArrayDupeKey(tag._d))
      dupeKeyCounter.set(tag._d, count + 1);
    tags.push(ctx);
    if (tag.tag === "title") {
      dom.title = tag.textContent;
      track("title", "", () => dom.title = state._t);
      continue;
    }
    ctx.$el = state._e.get(id);
    if (ctx.$el)
      trackCtx(ctx);
    else if (HasElementTags.has(tag.tag))
      pending.push(ctx);
  }
  for (const ctx of pending) {
    ctx.$el = dom.createElement(ctx.tag.tag);
    trackCtx(ctx);
    (frag[ctx.tag.tagPosition || "head"] ??= dom.createDocumentFragment()).appendChild(ctx.$el);
  }
  if (frag.head)
    dom.head.appendChild(frag.head);
  if (frag.bodyOpen)
    dom.body.insertBefore(frag.bodyOpen, dom.body.firstChild);
  if (frag.bodyClose)
    dom.body.appendChild(frag.bodyClose);
  for (const k in state._p)
    state._p[k]();
  head._dom = state;
  callHook(head, "dom:rendered", { renders: tags });
  head._du = false;
  head.dirty = false;
  return true;
}

export { createDomRenderer as c, renderDOMHead as r };
